// The single platform URL allowlist (also rejects protocol-relative "//host").
import { isSafeUrl } from './url';

// Minimal, XSS-safe markdown -> HTML for imported Strapi richtext (markdown).
// Strategy: escape ALL HTML first, THEN apply a limited markdown subset over the
// escaped text — so any raw HTML/script in the source becomes inert text and
// only tags WE emit reach the DOM. No sanitizer dependency needed. Supports
// headings (# .. ######), paragraphs (with soft line breaks), **bold**,
// *italic*, [text](url) links, and autolinked bare URLs + emails — all
// restricted to safe protocols.

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
  );
}

// A null-byte sentinel that cannot occur in the escaped text — used to stash
// already-emitted anchors/images while autolinking so restore can never collide
// with real content (e.g. a bare number like "5" surrounded by spaces).
const NUL = String.fromCharCode(0);

// Autolink bare URLs and email addresses (Strapi legal/richtext content stores
// them as plain text, but the live site renders them as clickable links).
// Anchors/images we already emitted are stashed first so a URL inside an href
// is never double-linked.
function autolink(s: string): string {
  const stash: string[] = [];
  s = s.replace(/<a\b[^>]*>[\s\S]*?<\/a>|<img\b[^>]*\/?>/gi, (m) => {
    stash.push(m);
    return NUL + (stash.length - 1) + NUL;
  });
  // Bare http(s) URLs (`&` is already escaped to `&amp;`, valid in an href).
  // Don't swallow trailing sentence punctuation.
  s = s.replace(/\bhttps?:\/\/[^\s<>"')\]]+/gi, (u) => {
    const core = u.replace(/[.,;:!?]+$/, '');
    return `<a href="${core}" rel="noreferrer">${core}</a>${u.slice(core.length)}`;
  });
  // Email addresses -> mailto:
  s = s.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, (e) => `<a href="mailto:${e}">${e}</a>`);
  return s.replace(new RegExp(NUL + '(\\d+)' + NUL, 'g'), (_m, i) => stash[Number(i)]!);
}

function inline(text: string): string {
  let s = escapeHtml(text);
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  // Images (![alt](url)) MUST run before links so the leading `!` isn't left
  // behind by the link rule matching the [alt](url) tail.
  s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt: string, url: string) =>
    isSafeUrl(url) ? `<img src="${url}" alt="${alt}" loading="lazy" />` : '',
  );
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, txt: string, url: string) =>
    isSafeUrl(url) ? `<a href="${url}" rel="noreferrer">${txt}</a>` : txt,
  );
  return autolink(s);
}

export function markdownToSafeHtml(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) {
      out.push(`<p>${para.map(inline).join('<br/>')}</p>`);
      para = [];
    }
  };
  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    const h = t.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flush();
      const level = h[1]!.length;
      out.push(`<h${level}>${inline(h[2]!)}</h${level}>`);
      continue;
    }
    para.push(t);
  }
  flush();
  return out.join('\n');
}
